
import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UsersApi, ApiUser, ApiUserAuthToken, ApiOfferSet, ApiNewsletterOptins } from '@tytapp/api';
import { FormComponent, FormHandlingComponent, FormInput, PageComponent, AppConfig, Redirection, PageService, Shell } from '@tytapp/common';
import { Alphanumeric, CheckedValidator, EmailValidator, NonEmptyValidator, ObjectValidator, SixCharacterLength, Validated } from '@tytapp/validation';
import { UserService } from '@tytapp/user';
import { environment } from 'environments/environment';

import { appendSearchParams, buildBooleanSearchParams, parseBooleanSearchParams } from '@tytapp/common/url-utils';
import { take } from 'rxjs/operators';

@Component({
  selector: 'tyt-signup-offers-step',
  templateUrl: './signup-offers-step.component.html',
  styleUrls: ['./signup-offers-step.component.scss']
})
export class SignupOffersStepComponent extends PageComponent implements FormHandlingComponent, OnInit {
  private route = inject(ActivatedRoute);
  private userService = inject(UserService);
  private redirection = inject(Redirection);
  private userApi = inject(UsersApi);

  readonly smsAgreement = this.userService.smsAgreement;

  @Input()
  offerSet : ApiOfferSet;

  @Validated({ class: NonEmptyValidator, field: 'firstName' })
  @FormInput()
  firstName: string;

  @Validated({ class: NonEmptyValidator, field: 'email' })
  @Validated({ class: EmailValidator, field: 'email' })
  @FormInput()
  email: string;

  @Validated({ class: NonEmptyValidator, field: 'password' })
  @Validated({ class: SixCharacterLength, field: 'password' })
  @FormInput()
  password: string;

  @Validated({ class: NonEmptyValidator, field: 'username' })
  @Validated({ class: Alphanumeric, field: 'username' })
  @FormInput()
  username: string;

  @FormInput()
  phoneNumber: string;

  @FormInput()
  zipCode: string;

  @Validated({ class: CheckedValidator, field: 'termsAgreement' })
  @FormInput()
  termsAgreement: boolean = false;

  @Validated({ class: NonEmptyValidator, field: 'recaptcha', validateWhen: (step : SignupOffersStepComponent) => step.showRecaptchaOnSignup })
  @FormInput()
  reCaptchaResolvedKey: string;

  errorMessage : string;

  @Input()
  mode: string;

  @Input()
  knownEmail: string;

  @Input()
  knownFirstName: string;

  submitting = false;
  submittingMessage = 'Setting up your account...';

  externalSignupOptions = [
    { alt: 'Sign In With Facebook', id: 'facebook', image: '/assets/sign-in/facebook.png' },
    { alt: 'Sign In With Google', id: 'google', image: '/assets/sign-in/google.png' },
    //{ id: 'twitter', image: '/assets/sign-in/twitter.png' },
    //{ id: 'youtube', image: '/assets/sign-in/youtube.png' },
  ]

  user : ApiUser;

  offerUUID : string;
  offerSetUUID : string;

  optins: ApiNewsletterOptins = {};

  public recaptchaSiteKey;
  public showRecaptchaOnSignup: boolean;

  async init() {

    this.recaptchaSiteKey = environment.recaptchaV2SiteKey;
    this.showRecaptchaOnSignup = await this.shell.hasFeature('apps.accounts.show_recaptcha_on_signup');

    this.shell.title = 'Sign up';
    this.subscribe(this.route.paramMap, params => {
      this.offerSetUUID = params.get('id');
      this.offerUUID = params.get('offerId');

      if (this.offerSetUUID == 'null')
        this.offerSetUUID = null;
      if (this.offerUUID == 'null')
        this.offerUUID = null;
    });

    let clientSide = typeof window !== 'undefined';
    this.subscribe(this.userService.userChanged, async user => {
      this.user = user;

      // If a user is already set then we need to enter submission mode
      // If we are returning from Social Login we don't want to show the
      // signup form via SSR before CSR takes over, sends signup analytics,
      // and redirects the user to the next step.

      if (user)
        this.submitting = true;

      // We need justLoggedIn here to avoid taking action while processing
      // a user signup (via email). This code should only run in the scenario
      // where the user is already signed in when the page loads.
      //
      // Also:
      // This code must only run on the client side. If it runs on the server side,
      // there will be no opportunity to send analytics beacons.

      if (clientSide && this.user && this.offerUUID && !this.justLoggedIn) {
        this.justLoggedIn = true; // ensure we do this only once
        let queryParams = await this.route.queryParamMap.pipe(take(1)).toPromise();
        if (queryParams.get('onboarded') === '1') {
          console.log(`Sending social signup analytics....`);
          await this.sendSocialSignupAnalytics();

            this.saveCommPrefsFromParams(queryParams.getAll('comm-pref'));
        }


        let memberBrowsingOTC =
          this.user.membership
          && this.user.membership.active
          && this.hasOneTimeOffers
        ;

        this.redirectToPaymentStep();
      }
    });

    if(this.knownEmail) {
      this.email = this.knownEmail;
    }

    if(this.knownFirstName) {
      this.firstName = this.knownFirstName;
    }
  }

  justLoggedIn = false;

    private saveCommPrefsFromParams(commPrefs: string[]) {
        if (this.user && commPrefs.length) {
            const optins = parseBooleanSearchParams(commPrefs);
            this.userService.saveCommPrefs(optins);
        }
    }

  async sendSocialSignupAnalytics() {
    await this.userService.sendBeacons(`Social Signup [Offers v2]`, () => {
        this.userService.sendBeaconGA4('sign_up', {
            method: 'social',
            app: 'tyt_accounts',
            path: 'offers_v2',
            offer_set: this.offerSetUUID,
            offer: this.offerUUID,
            offers_version: 2,
            // social_provider: '' // TODO
        });
        this.userService.sendBeaconFB('CompleteRegistration', 'offersv2_social_registered', {
          registration_type: 'tyt_account'
        });
    });
  }

  oauthUrlFor(provider : string) {
    let params = `app=accounts&source=${encodeURIComponent(this.source)}`;
    if (this.userService.sourceFirstTime !== null)
      params += `&source_first_time=${encodeURIComponent(this.userService.sourceFirstTime ? 'true' : 'false')}`;

    let returnURL = `/join`;
    if (typeof window !== 'undefined') {
      returnURL = `${window.location.href}`;
    }

      returnURL = appendSearchParams(returnURL, buildBooleanSearchParams('comm-pref', this.optins));
    params += `&return_url=${encodeURIComponent(returnURL)}`;

    return `${environment.urls.platform}/sign-in-with/${provider}?${params}`;
  }

  /**
   * Redirect to payment step, skipping browser history entry (to avoid "double-back" issues)
   */
  redirectToPaymentStep() {
    if (!this.offerUUID)
      this.redirection.go(`/join`);
    else
      this.redirection.go(`/join/m/${this.offerSetUUID}/${this.offerUUID}/payment`);
  }

  redirectToPricingStep() {
    if (!this.offerUUID)
      this.redirection.go(`/join`);
    else
      this.redirection.go(`/join/m/${this.offerSetUUID}/x/pricing`);
  }

  get ytcmMigrationOffer() {
    if (!this.offerSet)
      return false;

    let offer = this.offerSet.offers.find(x => x.uuid === this.offerUUID);
    return offer && offer.membership_product && offer.membership_product.product_sku === 'youtube_channel_membership';
  }

  /**
   * Navigate to payment step, creating a browser history entry
   */
  navigateToPaymentStep() {
    if (!this.offerUUID)
      this.router.navigateByUrl(`/join`);
    else
      this.router.navigateByUrl(`/join/m/${this.offerSetUUID}/${this.offerUUID}/payment`);
  }


  navigateToPricingStep() {
    if (!this.offerUUID)
      this.router.navigateByUrl(`/join`);
    else
      this.router.navigateByUrl(`/join/m/${this.offerSetUUID}/x/pricing`);
  }

  signInEmail : string;
  signInPassword : string;

  async recordSignupEvent(user : ApiUser) {
    await this.userService.sendBeacons('User Registration [Offers v2]', () => {

      let offerCombination : string;
      if (this.offerSetUUID)
        offerCombination = `${this.offerSetUUID}/${this.offerUUID}`;
      else
        offerCombination = `UNKNOWN/UNKNOWN`;

      this.userService.sendBeaconGA4('sign_up', {
          method: 'email_and_password',
          app: 'tyt_accounts',
          path: 'offers_v2',
          offer_set: this.offerSetUUID,
          offer: this.offerUUID,
          offers_version: 2
      });
      this.userService.sendBeaconFB('CompleteRegistration', offerCombination, {
        registration_type: 'tyt_account',
        offer_set_id: this.offerSetUUID,
        offer_id: this.offerUUID,
        offers_version: 2,
        method: 'offersv2'
      });
    });
  }

  private timeout(time = 0): Promise<void> {
    return new Promise(resolve => setTimeout(() => resolve(), time));
  }

  async resetPassword() {
    // CRITICAL SECTION: NO ASYNC
    if (this.submitting)
      return;
    this.error = false;
    this.submitting = true;
    this.submittingMessage = 'Finding your account...';
    // END CRITICAL SECTION: NO ASYNC

    let response : any;

    try {
      response = await this.userApi
        .requestPasswordReset({
          email: this.signInEmail,
          return_url: window.location.href
        })
        .toPromise()
      ;
    } catch (e) {
      if (e.json)
        e = e.json();
      this.errorMessage = `${e.message || e.error || e}`;
      this.error = true;
      this.submitting = false;
      return;
    }

    this.submitting = false;
    this.mode = 'reset:done';
    this.shell.title = 'Recovery Email Sent';
  }

  async signIn() {
    // CRITICAL SECTION: NO ASYNC
    if (this.submitting)
      return;

    this.error = false;
    this.submitting = true;
    this.submittingMessage = 'Signing in...';
    // END CRITICAL SECTION: NO ASYNC

    let userToken : ApiUserAuthToken;

    try {
      userToken = await this.userApi.authenticate({
        method: 'tyt',
        email: this.signInEmail,
        password: this.signInPassword
      }).toPromise();
    } catch (e) {
      console.error(`Caught error while authenticating:`);
      console.error(e);
      if (e.json)
        e = e.json();

      console.error(`Unwrapped error:`);
      console.error(e);

      this.error = true;
      this.errorMessage = `${e.message || e.error || e}`;
      this.submitting = false;
      return;
    }

    this.submittingMessage = `Welcome ${userToken.display_name}! Just a second...`;
    await this.timeout(2000);

    await this.userService.completeLoginInPlace(<any>userToken, userToken.token);

    let memberBrowsingOTC =
      this.user.membership
      && this.user.membership.active
      && this.hasOneTimeOffers
    ;
  }

  private get hasOneTimeOffers() {
    if (!this.offerSet || !this.offerSet.offers)
      return false;

    return this.offerSet.offers.some(x => x.period === 'one_time');
  }

  /**
   * Some common sense corrections for common typos
   */
  correctEmail() {
    if (!this.email)
      return;

    if (this.email.endsWith('.con')) {
      this.email = this.email.replace(/\.con$/, '.com');
    }

    if (this.email.endsWith('.comm')) {
      this.email = this.email.replace(/\.comm$/, '.com');
    }

    if (this.email.endsWith('@gmail')) {
      this.email = this.email.replace(/@gmail/, '@gmail.com');
    }

    if (this.email.endsWith('@icloud')) {
      this.email = this.email.replace(/@gmail/, '@icloud.com');
    }

    if (this.email.endsWith('@me')) {
      this.email = this.email.replace(/@gmail/, '@me.com');
    }
  }

  get source() {
    return this.userService.source || `modern-offerset:${this.offerSetUUID}`;
  }

  @ViewChild('form')
  form: FormComponent;

  async submit() {
    this.correctEmail();

    if (!this.form.validate())
      return;

    // CRITICAL SECTION: NO ASYNC
    if (this.submitting)
      return;

    this.error = false;
    this.submitting = true;
    this.submittingMessage = 'Setting up your account...';
    // END CRITICAL SECTION: NO ASYNC

    // Be extra sure the UI has updated before we do anything
    await this.timeout(100)

    try {
      let userAuthToken = await this.userApi.signup({
        app: 'accounts',
        flow: 'offers-v2',
        email: this.email,
        password: this.password,
        first_name: this.firstName,
        source: this.source,
        source_first_time: this.userService.sourceFirstTime,
        recaptcha_token: this.reCaptchaResolvedKey,
        username: this.username,
        phone_number: this.phoneNumber,
      }).toPromise();

      let user : ApiUser = userAuthToken;

      this.justLoggedIn = true;
      this.userService.saveAuthentication(user, userAuthToken.token);
      await this.recordSignupEvent(user);

      if (userAuthToken.token) {
        this.userService.completeLogin(user, userAuthToken.token, false, () => {
          // probably route them to home page as a logged in user
          // this.shell.showDialog(
          //   'success',
          //   'Registration is successful',
          //   'Your account has been created'
          // );
          this.navigateToPaymentStep();
          // alert("Registration is successful!, check your email to confirm.  Alert will be replaced with redirect to payment page in future");
        });
      } else {
        this.shell.alert(
          'Confirm your Account to login',
          'Your account has been created, but you must confirm your account before you login. Please check your email and follow the instructions we\'ve sent.'
        );

        this.submitting = false;
        this.mode = 'signin';
      }

    } catch (e) {
      if (e.json)
        e = e.json();

      console.error(`Received error while signing up:`);
      console.error(e);

      if (e.code === 'email_exists') {
        this.mode = 'signin';
        this.error = true;
        this.errorMessage = `Looks like you already have a TYT account! Enter your password below to continue.`;
        this.signInEmail = this.email;
      } else {
        this.error = true;
        this.errorMessage = e.message || e.error;
      }

      setTimeout(() => this.submitting = false, 3000);
    }

    this.userService.saveCommPrefs(this.optins);


  }

  get accountSettingsUrl() {
    return `${environment.urls.app}/settings/account?t=connected-accounts`;
  }

  checkKeyOnFieldErrorsArray(key: string): boolean {
    if (!this.fieldErrors)
      return false;
    return !!this.fieldErrors.find(k => k.field === key);
  }

  captchaSolved(response : string) {
    console.log(`reCAPTCHA response: ${response}`);
    this.reCaptchaResolvedKey = response;
  }

  get isFormValid() {
    const formValidator = new ObjectValidator(this);
    return formValidator.validate() === true;
  }

  goToSignUp() {
    this.mode = 'signup';
    this.shell.title = 'Sign up';
  }

  goToSignIn() {
    this.mode = 'signin';
    this.shell.title = 'Sign in';
  }

  goToReset() {
    this.mode = 'reset';
    this.shell.title = 'Reset password';
  }
}
